<script lang="ts">
  import { storyblokEditable } from "@storyblok/svelte";
  import gsap from "gsap";
  import { onMount } from "svelte";
  import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
    import type { SectionStoryblok } from "$types/components";

  export let blok: SectionStoryblok;
  export let scaleBackground = false;

  $: backgroundColor =
    blok.backgroundColor && blok.backgroundColor.length > 0
      ? blok.backgroundColor[0].color
      : "";
  let background: HTMLDivElement;
  let tl: ReturnType<typeof gsap.timeline>;
  let sectionWrapper: HTMLDivElement;
  let scroller;
  export let onProgress: ((a1: number, a2: number) => void) | null = null;
  export let section = null;

  onMount(() => {
    if (background && scaleBackground) {
      tl = gsap.timeline({ paused: true });
      const a = { v: 0 };

      tl.to(a, { v: 1, duration: 1, ease: "none" }, 0);

      const height = sectionWrapper.getBoundingClientRect().height;
      const timeIn = (window.innerHeight * 0.7) / (height + window.innerHeight);

      tl.to(
        background,
        {
          scaleX: (window.innerWidth + 20) / (window.innerWidth - 40),
          duration: timeIn * 0.8,
          ease: "none",
        },
        timeIn,
      );

      tl.to(
        background,
        {
          scaleX: 1,
          duration: timeIn,
          ease: "none",
        },
        1 - timeIn * 2,
      );

      scroller = ScrollTrigger.create({
        trigger: sectionWrapper,
        start: "top bottom",
        end: "bottom top",
        scrub: 1,
        onUpdate: (e) => {
          tl.progress(e.progress);
          onProgress && onProgress(e.progress, height);
        },
      });
    }
  });
</script>

<!-- @tailwind unpurge bg-[rgb(var(--color-white))] bg-[rgb(var(--color-yellow-primary))] bg-[rgb(var(--color-green-primary))]
bg-[rgb(var(--color-green-secondary))] bg-[rgb(var(--color-blue-primary))] bg-[rgb(var(--color-black))] bg-[rgb(var(--color-warm-grey))] -->

<section
  id={blok.hash}
  class={`section ${$$restProps.class} ${backgroundColor} ${
    blok.paddingTop ? "pt-${blok.paddingTop}" : ""
  }`}
  use:storyblokEditable={blok}
  bind:this={section}
>
  <div
    class="section-inner {backgroundColor ? 'has-background' : ''}"
    bind:this={sectionWrapper}
  >
    <slot name="outside-wrapper" />
    {#if backgroundColor}
      <div
        bind:this={background}
        class={`section-background background-${backgroundColor}`}
      />
    {/if}
    <div class="wrapper">
      <slot />
    </div>
  </div>
</section>

<style lang="postcss">
  .section {
    width: 100%;
    padding-top: 70px;
    position: relative;
    z-index: 5;

    @screen md {
      padding-top: 80px;
    }
    @screen lg {
      padding-top: 120px;
    }

    &.top-pad-none {
      padding-top: 0;
    }

    &.top-pad-none {
      @apply pt-0;
    }

    &.top-pad-xsmall {
      @apply pt-4;
    }

    &.top-pad-small {
      @apply pt-6;
    }

    &.top-pad-medium {
      @apply pt-8;
    }

    &.top-pad-large {
      @apply pt-10;
    }

    &.top-pad-extra-large {
      @apply pt-16;
    }

    &.top-pad-2x-extra-large {
      @apply pt-24;
    }

    &.pt-small {
      padding-top: 48px;
      @screen md {
        padding-top: 60px;
      }
      @screen lg {
        padding-top: 90px;
        max-width: 1440px;
      }
    }

    &.pt-mid {
      padding-top: 60px;
      @screen md {
        padding-top: 70px;
      }
      @screen lg {
        padding-top: 100px;
      }
    }

    &.pt-tab {
      padding-left: 20px !important;
      padding-right: 20px !important;
    }

    &.pt-newsroom {
      padding-top: 64px;
      @screen lg {
        padding-top: 80px;
      }
      @screen xl {
        max-width: 1480px;
      }
    }

    &.pt-blog {
      padding-top: 24px;
      @screen md {
        padding-top: 40px;
      }
      @screen lg {
        padding-top: 48px;
      }
    }

    &.section-webinars {
      padding-top: 88px;
      margin-bottom: -32px;
      @screen sm {
        margin-bottom: -8px;
      }
      @screen md {
        padding-top: 80px;
      }
    }

    .marker {
      position: fixed;
      right: 0;
      top: calc(50% - 1px);
      width: 50%;
      height: 2px;
      background: red;
    }

    .section-inner {
      position: relative;

      &.has-background {
        padding: 60px 0;
        @screen lg {
          padding: 100px 0 110px;
        }
      }
    }

    &.has-slider {
      z-index: 6;

      .section-inner {
        padding-bottom: 0;

        @include breakpoint("small") {
          padding-bottom: 80px;
        }
      }

      &.fullscreen-gallery {
        .section-inner {
          padding-bottom: 80px;
          @screen lg {
            padding-bottom: 100px;
          }
        }
      }

      .section-inner.has-background {
        padding-bottom: 80px;
        @screen lg {
          padding-bottom: 100px;
        }
      }
    }

    &:global(.section-header) {
      .eyebrow {
        margin-bottom: 16px;
        @screen md {
          margin-bottom: 24px;
        }
      }
    }

    .section-background {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 1;

      @include breakpoint("small") {
        border-radius: var(--border-radius-section);
      }
    }

    .wrapper {
      position: relative;
      z-index: 5;
    }
  }
</style>
